import { pickBy } from 'lodash';
import LogRocket from 'logrocket';

import type { Customer } from 'src/data/customer';
// @ts-expect-error - data modules don't have TypeScript declarations yet
import { getCustomerPlan } from 'src/data/subscription';
import { isApplicationModeInfinity } from 'src/data/system';
import {
  getUserCompanyPositions,
  getUserCompanySize,
  getUserCountry,
  getUserRoleForCustomer,
  type User,
} from 'src/data/user';
import * as env from 'src/lib/env';

type EventAttributes = Record<string, string | number | boolean>;

let inited = false;
let started = false;

export const init = (): void => {
  // We don't want to use LogRocket in Infinity Extension sessions
  if (isApplicationModeInfinity()) {
    return;
  }

  inited = true;
};

export const start = (): void => {
  if (!inited) {
    return;
  }

  const appId = env.getLogRocketAppId();
  if (!appId) {
    return;
  }

  try {
    LogRocket.init(appId, {
      release: env.getRepositoryTag(),
    });
    started = true;
  } catch {
    // We don't care about this error
  }
};

interface LogRocketIdentifyParams {
  user: User;
  customer: Customer;
}

export const identifyUser = ({ user, customer }: LogRocketIdentifyParams): void => {
  if (!started || !user || !customer) {
    return;
  }

  try {
    const userAttributes: EventAttributes = {
      name: user.name || '',
      email: user.email,
      country: getUserCountry(user) || '',
      companySize: getUserCompanySize(user) || '',
      companyPosition: getUserCompanyPositions(user).join(', '),
      createdAt: user.createdAt,
      customerId: customer.id,
      role: getUserRoleForCustomer(user, customer.id) || '',
      plan: getCustomerPlan(customer),
    };

    LogRocket.identify(String(user.id), userAttributes);
  } catch {
    // We don't care about this error
  }
};

export const trackEvent = (eventName: string, attributes: EventAttributes): void => {
  if (!started) {
    return;
  }

  // Filter out null or undefined attributes
  const preparedAttributes = pickBy(attributes, (value) => value != null);

  try {
    LogRocket.track(eventName, preparedAttributes);
  } catch {
    // We don't care about this error
  }
};

export const captureError = (
  message: string,
  extra?: Record<string, string | number | boolean>
): void => {
  if (!started) {
    return;
  }

  try {
    LogRocket.captureMessage(message, { extra });
  } catch {
    // We don't care about this error
  }
};
